import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Gift, Lock, Sparkles } from "lucide-react";
import { VOLUME_TIERS, nextVolumeTier, volumeTierFor } from "@/data/products";

/**
 * Volume rewards ladder. `count` = trackers in the order. Discounts + perks escalate with quantity.
 * compact → single-row summary (cart). full → tier cards (shop / fleet).
 */
export const NextTierNudge = ({ count, className = "" }) => {
  const next = nextVolumeTier(count);
  if (!next) return null;
  const need = next.min - count;
  const prevMin = volumeTierFor(count)?.min || 0;
  const pct = Math.min(100, ((count - prevMin) / (next.min - prevMin)) * 100);
  return (
    <div data-testid="volume-next-nudge" className={`rounded-2xl border border-tracksy-orange/30 bg-tracksy-orange/5 p-4 ${className}`}>
      <p className="text-sm font-bold text-tracksy-ink">
        Add {need} more tracker{need === 1 ? "" : "s"} to unlock <span className="text-tracksy-orange">{next.name}</span> — {next.headline} + {next.perks[0]}
      </p>
      <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-black/5">
        <motion.div className="h-full rounded-full bg-tracksy-orange" initial={false} animate={{ width: `${pct}%` }} transition={{ type: "spring", stiffness: 120, damping: 20 }} />
      </div>
    </div>
  );
};

const VolumeRewards = ({ count = 0, showNudge = true, dark = false }) => {
  const reached = volumeTierFor(count);
  return (
    <div data-testid="volume-rewards" className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {VOLUME_TIERS.map((t, i) => {
          const unlocked = count >= t.min;
          const current = reached?.id === t.id;
          return (
            <motion.div
              key={t.id}
              data-testid={`volume-tier-${t.id}`}
              data-unlocked={unlocked}
              animate={{ scale: current ? 1.02 : 1 }}
              className={`relative flex h-full flex-col rounded-2xl border p-4 transition-colors ${
                current
                  ? "border-tracksy-orange bg-white shadow-glow"
                  : unlocked
                  ? "border-tracksy-green/40 bg-white"
                  : dark
                  ? "border-white/10 bg-white/5"
                  : "border-black/5 bg-white/70"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-[10px] font-extrabold uppercase tracking-widest ${unlocked ? "text-tracksy-orange" : dark ? "text-white/50" : "text-tracksy-muted"}`}>{t.min}+ trackers</span>
                {unlocked ? (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-tracksy-green text-white"><Check className="h-3 w-3" /></span>
                ) : (
                  <Lock className={`h-3.5 w-3.5 ${dark ? "text-white/30" : "text-tracksy-muted/60"}`} />
                )}
              </div>
              <p className={`mt-2 font-heading text-base font-bold ${dark && !unlocked ? "text-white" : "text-tracksy-ink"}`}>{t.name}</p>
              <p className="font-heading text-2xl font-extrabold tracking-tight text-tracksy-orange">{t.headline}</p>
              <ul className="mt-3 space-y-1.5">
                {t.perks.map((p) => (
                  <li key={p} className={`flex gap-2 text-xs leading-relaxed ${dark && !unlocked ? "text-white/60" : "text-tracksy-sub"}`}>
                    <Gift className="mt-0.5 h-3.5 w-3.5 shrink-0 text-tracksy-orange" />
                    {p}
                  </li>
                ))}
              </ul>
              {current && (
                <span className="absolute -top-2.5 left-4 inline-flex items-center gap-1 rounded-full bg-tracksy-orange px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-widest text-white">
                  <Sparkles className="h-3 w-3" /> Your tier
                </span>
              )}
              {i === VOLUME_TIERS.length - 1 && (
                <Link to="/fleet#enquiry" className="mt-3 whitespace-nowrap text-[11px] font-bold text-tracksy-orange hover:underline">Tailored pricing →</Link>
              )}
            </motion.div>
          );
        })}
      </div>
      {showNudge && <NextTierNudge count={count} />}
    </div>
  );
};

export default VolumeRewards;
